import axios from 'axios';

const api = axios.create({ baseURL: '/api' });

const TOKEN_KEY = 'tb_token';

// AuthContext registers its logout handler here so a 401 anywhere logs out
let onAuthFailure = null;
export const setAuthFailureHandler = (fn) => { onAuthFailure = fn; };

api.interceptors.request.use(config => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  res => res,
  err => {
    // Expired/invalid session → log out (but not for login/register failures)
    if (err.response?.status === 401 && !err.config?.url?.startsWith('/auth/')) {
      onAuthFailure?.();
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  register:     (data)  => api.post('/auth/register', data),
  login:        (data)  => api.post('/auth/login', data),
  me:           ()      => api.get('/auth/me'),
  mfaVerify:    (data)  => api.post('/auth/mfa/verify', data),
  mfaSetup:     ()      => api.post('/auth/mfa/setup'),
  mfaEnable:    (data)  => api.post('/auth/mfa/enable', data),
  mfaDisable:   (data)  => api.post('/auth/mfa/disable', data),
  verifyEmail:  (token) => api.get('/auth/verify', { params: { token } }),
  resendVerify: (email) => api.post('/auth/verify/resend', { email }),
  deleteAccount:(data)  => api.post('/auth/delete-account', data),
};

export const monthsApi = {
  getAll:        ()            => api.get('/months'),
  init:          (year, month) => api.post(`/months/init?year=${year}&month=${month}`),
  createNext:    (id)          => api.post(`/months/${id}/next`),
  updateNotes:   (id, data)    => api.put(`/months/${id}/notes`, data),
};

export const categoriesApi = {
  getAll: (monthId)       => api.get(`/months/${monthId}/categories`),
  add:    (monthId, data) => api.post(`/months/${monthId}/categories`, data),
  update: (id, data)      => api.put(`/categories/${id}`, data),
  delete: (id)            => api.delete(`/categories/${id}`),
  // Defaults to this month forward so a roll-forward doesn't undo the move
  move:   (id, groupName, applyToFuture = true) =>
            api.put(`/categories/${id}/group`, { groupName, applyToFuture }),
};

export const transactionsApi = {
  getAll:      (monthId)             => api.get(`/months/${monthId}/transactions`),
  getByAcct:   (monthId, acctName)   => api.get(`/months/${monthId}/transactions/account/${encodeURIComponent(acctName)}`),
  add:         (monthId, data)       => api.post(`/months/${monthId}/transactions`, data),
  update:      (id, data)            => api.put(`/transactions/${id}`, data),
  delete:      (id)                  => api.delete(`/transactions/${id}`),
};

export const accountsApi = {
  getAll: (monthId)       => api.get(`/months/${monthId}/accounts`),
  add:    (monthId, data) => api.post(`/months/${monthId}/accounts`, data),
  update: (id, data)      => api.put(`/accounts/${id}`, data),
  delete: (id)            => api.delete(`/accounts/${id}`),
};

export const goalsApi = {
  getAll:  ()          => api.get('/goals'),
  add:     (data)      => api.post('/goals', data),
  update:  (id, data)  => api.put(`/goals/${id}`, data),
  delete:  (id)        => api.delete(`/goals/${id}`),
};

// ── Flexible household model ────────────────────────────────────────────────

export const membersApi = {
  getAll: ()         => api.get('/members'),
  add:    (data)     => api.post('/members', data),
  update: (id, data) => api.put(`/members/${id}`, data),
  delete: (id)       => api.delete(`/members/${id}`),
};

export const savingsPlansApi = {
  getAll: ()         => api.get('/savings-plans'),
  add:    (data)     => api.post('/savings-plans', data),
  update: (id, data) => api.put(`/savings-plans/${id}`, data),
  delete: (id)       => api.delete(`/savings-plans/${id}`),
};

export const debtPlansApi = {
  getAll: ()         => api.get('/debt-plans'),
  add:    (data)     => api.post('/debt-plans', data),
  update: (id, data) => api.put(`/debt-plans/${id}`, data),
  delete: (id)       => api.delete(`/debt-plans/${id}`),
};

export const spendingGoalsApi = {
  getAll: ()         => api.get('/spending-goals'),
  add:    (data)     => api.post('/spending-goals', data),
  update: (id, data) => api.put(`/spending-goals/${id}`, data),
  delete: (id)       => api.delete(`/spending-goals/${id}`),
};

export const householdSettingsApi = {
  get:    ()     => api.get('/household-settings'),
  update: (data) => api.put('/household-settings', data),
};

export const incomesApi = {
  get:     (monthId)       => api.get(`/months/${monthId}/incomes`),
  replace: (monthId, rows) => api.put(`/months/${monthId}/incomes`, rows),
};

export const historyApi = {
  // months omitted = the user's whole history
  categories:  (months) => api.get('/history/categories', { params: months ? { months } : {} }),
  suggestions: (window) => api.get('/history/suggestions', { params: window ? { window } : {} }),
  applySuggestion: (spendingGoalId, amount) =>
    api.post('/history/suggestions/apply', { spendingGoalId, amount }),
};

export const blogApi = {
  published: ()        => api.get('/blog'),
  mine:      ()        => api.get('/blog/mine'),
  canPost:   ()        => api.get('/blog/can-post'),
  submit:    (data)    => api.post('/blog', data),
  report:    (id)      => api.post(`/blog/${id}/report`),
  queue:     ()        => api.get('/blog/admin'),                        // admin only
  moderate:  (id, status, note) => api.put(`/blog/admin/${id}`, { status, note }), // admin only
  remove:    (id)      => api.delete(`/blog/admin/${id}`),               // admin only
};

export const feedbackApi = {
  submit:    (data)        => api.post('/feedback', data),
  getAll:    ()            => api.get('/feedback'),            // admin only
  setStatus: (id, status)  => api.put(`/feedback/${id}/status`, { status }), // admin only
};
